/**
 * Stok ürünü görüntüleme (salt okunur).
 *
 * TEK KAYNAK: hem Satış/Stok ekranı hem Raporlar → Satış/Stok tablosu bu
 * bileşeni kullanır; iki yerde ayrı görüntüleme ekranı YOKTUR. Bu yüzden
 * `pages/SalesStock.jsx` içinden buraya taşındı — içeriği değişmedi, yalnızca
 * not gösterimi düzeltildi (aşağıya bakınız).
 *
 * NOT ALANLARI: arayüzde not, stok girişi (fatura) başlığında TEK ALAN olarak
 * girilir ve aynı girişteki tüm ürünler onu paylaşır → `entry_notes`. Ürüne
 * özel `notes` alanı ayrıdır (yalnızca ürün güncelleme ucundan yazılabilir) ve
 * doluysa AYRICA gösterilir; hiçbiri diğerini gizlemez.
 */
import { API_BASE } from "../lib/api";
import { fmtMoney } from "../lib/currency";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

/** Etiket + değer satırı. Değer boşsa "—" yazılır (alan gizlenmez) — amaç
 *  "o tipe ait TÜM alanlar eksiksiz görünsün". */
function ViewRow({ label, value, mono }) {
  const empty = value === null || value === undefined || value === "";
  return (
    <div className="space-y-0.5">
      <div className="label-eyebrow text-muted-foreground">{label}</div>
      <div className={`text-sm ${mono ? "font-mono" : ""} ${empty ? "text-muted-foreground" : ""}`}>
        {empty ? "—" : value}
      </div>
    </div>
  );
}

/** Stok girişinde girilen alanların tamamını ÜRÜN TİPİNE GÖRE gösterir.
 *  Lastik alanları jantta, jant alanları lastikte GÖSTERİLMEZ; Diğer
 *  Malzeme'de yalnızca ortak alanlar görünür. */
export default function ProductViewDialog({ product, onClose, meta, currency }) {
  if (!product) return null;

  const labelOf = (list, value) =>
    (meta?.[list] || []).find((x) => x.value === value)?.label || null;

  const isTyre = product.product_type === "lastik";
  const isRim = product.product_type === "jant";
  const money = (v) => fmtMoney(v, currency);
  const dateText = (v) => (v ? new Date(v).toLocaleDateString("tr-TR") : null);

  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="rounded-sm max-w-2xl max-h-[85vh] overflow-y-auto"
        data-testid="product-view-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">{product.name}</DialogTitle>
          <DialogDescription>
            Stok girişinde kaydedilen bilgiler. Bu ekran salt okunurdur.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" className="rounded-sm" data-testid="product-view-type">
              {product.product_type_label}
            </Badge>
            <Badge
              variant="outline"
              className="rounded-sm"
              data-testid="product-view-condition"
            >
              {product.condition_label || "Durum: —"}
            </Badge>
            {product.is_critical && (
              <Badge variant="outline" className="rounded-sm border-red-300 text-red-700">
                Kritik stok
              </Badge>
            )}
          </div>

          {product.photo_media_id && (
            <img
              src={`${API_BASE}/media/${product.photo_media_id}`}
              alt={product.name}
              className="max-h-48 rounded-sm border border-border object-contain"
              data-testid="product-view-photo"
            />
          )}

          {/* --- Genel --- */}
          <div>
            <div className="label-eyebrow mb-2">Genel</div>
            <div className="grid sm:grid-cols-3 gap-3">
              <ViewRow label="Ürün Adı" value={product.name} />
              <ViewRow label="Marka" value={product.brand} />
              <ViewRow label="Model" value={product.model} />
              {!(product.product_type === "diger") && (
                <ViewRow label="Kategori" value={labelOf("categories", product.category)} />
              )}
              <ViewRow label="Depo" value={product.warehouse_name} mono />
              <ViewRow label="Satıcı" value={product.supplier_name} />
              <ViewRow label="Fatura No" value={product.invoice_no} mono />
              <ViewRow label="Fatura Tarihi" value={dateText(product.invoice_date)} mono />
              <ViewRow label="Kayıt Tarihi" value={dateText(product.created_at)} mono />
            </div>
          </div>

          {/* --- Tipe özel: LASTİK --- */}
          {isTyre && (
            <div data-testid="product-view-tyre-fields">
              <div className="label-eyebrow mb-2">Lastik Bilgileri</div>
              <div className="grid sm:grid-cols-3 gap-3">
                <ViewRow label="Ebat" value={product.size} mono />
                <ViewRow label="Sezon" value={labelOf("seasons", product.season)} />
                <ViewRow label="Desen" value={product.pattern} />
                <ViewRow label="DOT" value={product.dot} mono />
                <ViewRow
                  label="Diş Derinliği (mm)"
                  value={product.tread_depth_mm ?? ""}
                  mono
                />
                <ViewRow label="Üretim Tarihi" value={product.production_date} mono />
              </div>
            </div>
          )}

          {/* --- Tipe özel: JANT --- */}
          {isRim && (
            <div data-testid="product-view-rim-fields">
              <div className="label-eyebrow mb-2">Jant Bilgileri</div>
              <div className="grid sm:grid-cols-3 gap-3">
                <ViewRow label="Jant Çapı (İnç)" value={product.rim_diameter_inch ?? ""} mono />
                <ViewRow label="Bijon Aralığı (PCD)" value={product.rim_pcd} mono />
                <ViewRow label="Genişlik / J" value={product.rim_width_j} mono />
                <ViewRow label="Ofset (ET)" value={product.rim_offset_et} mono />
                <ViewRow label="Göbek Çapı (CB)" value={product.rim_center_bore} mono />
                <ViewRow label="Malzeme" value={product.rim_material} />
              </div>
            </div>
          )}

          {/* --- Stok --- */}
          <div>
            <div className="label-eyebrow mb-2">Stok</div>
            <div className="grid sm:grid-cols-3 gap-3">
              <ViewRow label="Mevcut Stok" value={product.quantity} mono />
              <ViewRow label="Giriş Adedi" value={product.entry_quantity} mono />
              <ViewRow label="Kritik Stok" value={product.critical_quantity} mono />
            </div>
          </div>

          {/* --- Fiyatlar --- */}
          <div>
            <div className="label-eyebrow mb-2">Fiyatlar</div>
            <div className="grid sm:grid-cols-3 gap-3">
              <ViewRow
                label="Alış (Girilen)"
                value={`${money(product.purchase_price_input)} ${
                  product.purchase_kdv_included ? "(KDV dahil)" : "(KDV hariç)"
                }`}
                mono
              />
              <ViewRow label="Alış KDV Oranı" value={`%${product.purchase_kdv_rate}`} mono />
              <ViewRow label="Alış (KDV dahil)" value={money(product.purchase_price_gross)} mono />
              <ViewRow
                label="Satış (Girilen)"
                value={`${money(product.sale_price_input)} ${
                  product.sale_kdv_included ? "(KDV dahil)" : "(KDV hariç)"
                }`}
                mono
              />
              <ViewRow label="Satış KDV Oranı" value={`%${product.sale_kdv_rate}`} mono />
              <ViewRow label="Satış (KDV dahil)" value={money(product.sale_price_gross)} mono />
              <ViewRow
                label="Kâr Oranı"
                value={product.profit_rate != null ? `%${product.profit_rate}` : ""}
                mono
              />
            </div>
          </div>

          {/* --- Not --- */}
          <div>
            <div className="label-eyebrow mb-2">Not</div>
            <div
              className={`text-sm whitespace-pre-wrap ${
                product.entry_notes ? "" : "text-muted-foreground"
              }`}
              data-testid="product-view-notes"
            >
              {product.entry_notes || "—"}
            </div>
            {/* Ürüne özel not (varsa) — fatura notunu gizlemeden AYRICA gösterilir */}
            {product.notes && (
              <div className="mt-3">
                <div className="label-eyebrow mb-1">Ürün Notu</div>
                <div
                  className="text-sm whitespace-pre-wrap"
                  data-testid="product-view-item-notes"
                >
                  {product.notes}
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            className="rounded-sm"
            onClick={onClose}
            data-testid="product-view-close"
          >
            Kapat
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
